import type { API } from 'api/config/types';
import { CustomTimestamps } from 'chessboard/api/custom-timestamps/config/types';
import { toUnit } from 'chessboard/api/custom-timestamps/model/to-unit';
import { getValidTimestamp } from 'chessboard/api/custom-timestamps/model/get-valid-timestamp';
import { Core } from 'chessboard/core/config/types';
import { setTimestampOnNode } from 'chessboard/model/move/set-timestamp-on-node';
import { getTimestampFromNode } from 'chessboard/model/move/get-timestamp-from-node';

/**
 * Create the timestamps API.
 */
export function createTimestampsAPI(context: API.Context): CustomTimestamps.API {
  return {
    get,
    set,
    setMany,
  };

  /**
   * Get an array of timestamps, or an individual timestamp.
   */
  function get(
    index?: number,
    { filtered = true }: { filtered?: boolean } = {},
    unit = CustomTimestamps.Units.Deciseconds,
  ): CustomTimestamps.Timestamp[] | CustomTimestamps.Timestamp | undefined {
    const { api } = context;

    const timestamps = <CustomTimestamps.Timestamp[]>api.getLine(0)?.map(getTimestampFromNode);

    if (index !== undefined) {
      return timestamps[index] && toUnit(timestamps[index], unit);
    }

    /**
     * Filtering this list can shift the index of timestamps
     * relative to their associated move node index.
     */
    const timestampsArr = filtered
      ? timestamps.filter((timestamp) => timestamp !== undefined)
      : timestamps;

    return timestampsArr.map((timestamp) => toUnit(timestamp, unit));
  }

  /**
   * Get all current timestamps.
   */
  function getAll(): CustomTimestamps.Timestamp[] {
    return <CustomTimestamps.Timestamp[]>get(undefined, { filtered: false });
  }

  /**
   * Set a timestamp on the main line.
   */
  function set(
    timestamp: CustomTimestamps.Timestamp,
    index: number,
    unit = CustomTimestamps.Units.Deciseconds,
  ): API.Response | null {
    const { api, respond } = context;
    const { getLine } = api;

    const node = getLine(0)?.[index];

    if (!node) {
      return null;
    }

    const validTimestamp = getValidTimestamp(timestamp, unit);

    if (validTimestamp === undefined) {
      return null;
    }

    setTimestampOnNode({ api, node, timestamp: validTimestamp });

    return respond(Core.APIEvents.NodeUpdated, { changes: { timestamp: validTimestamp, node } });
  }

  /**
   * Set multiple timestamps on the main line.
   */
  function setMany(
    timestamps: CustomTimestamps.Timestamp[] = [],
    unit = CustomTimestamps.Units.Deciseconds,
  ): API.Response {
    const { api, respond } = context;

    /**
     * If passing an empty array or an undefined value, map all the existing
     * timestamps to null so that they are removed individually in the `set` method.
     */
    const timestampsToSet = timestamps.length === 0 ? getAll().map(() => null) : timestamps;

    api.getRawLines()[0].forEach((node, index) => {
      if (timestampsToSet[index] === undefined) {
        return;
      }

      const validTimestamp = getValidTimestamp(timestampsToSet[index], unit);

      if (validTimestamp !== undefined) {
        setTimestampOnNode({ api, node, timestamp: validTimestamp });
      }
    });

    return respond(Core.APIEvents.LineUpdated);
  }
}
